<script lang="ts">
  /**
   * Guided tour: dims the page, spotlights one feature at a time and explains it in a card next
   * to it. Switches panel tabs and scrolls as needed. Keys: → / Enter next, ← back, Esc leaves.
   */
  import { tick } from 'svelte';
  import type { AppController } from '../../app/controller.svelte';
  import { TOUR_STEPS, tourBodyParts } from '../tour';
  import Icon from './Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();

  const CARD_W = 350;
  const GAP = 16;
  const MARGIN = 16;

  const index = $derived(ctl.tourStep ?? 0);
  const step = $derived(TOUR_STEPS[index]!);
  const last = $derived(index === TOUR_STEPS.length - 1);

  let rect = $state<DOMRect | null>(null);
  let card = $state<HTMLDivElement>();
  let nextButton = $state<HTMLButtonElement>();
  let cardH = $state(200);
  let vw = $state(window.innerWidth);
  let vh = $state(window.innerHeight);

  function go(i: number) {
    ctl.tourStep = Math.max(0, Math.min(TOUR_STEPS.length - 1, i));
  }
  function next() {
    if (last) ctl.endTour();
    else go(index + 1);
  }

  // Each step: open the right tab, then find, scroll to and measure the target.
  $effect(() => {
    const s = step;
    if (s.tab || s.target) {
      ctl.panelOpen = true;
      if (s.tab) ctl.tab = s.tab;
    }
    rect = null;
    let cancelled = false;
    void tick().then(() => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (cancelled) return;
        const el = s.target ? document.querySelector(s.target) : null;
        el?.scrollIntoView({ block: 'center', inline: 'nearest' });
        measure();
        nextButton?.focus();
      });
    });
    return () => (cancelled = true);
  });

  function measure() {
    vw = window.innerWidth;
    vh = window.innerHeight;
    const el = step.target ? document.querySelector(step.target) : null;
    rect = el ? el.getBoundingClientRect() : null;
    if (card) cardH = card.offsetHeight;
  }

  // Layout can shift (tabs render, panels scroll): keep the spotlight on its target.
  $effect(() => {
    const id = setInterval(measure, 300);
    return () => clearInterval(id);
  });

  /** Card to the left of the target if there's room (the panel is on the right), else below/above. */
  const pos = $derived.by(() => {
    const w = Math.min(CARD_W, vw - 2 * MARGIN);
    if (!rect) return { left: (vw - w) / 2, top: Math.max(MARGIN, (vh - cardH) / 2), w };
    const clampTop = (t: number) => Math.max(MARGIN, Math.min(vh - cardH - MARGIN, t));
    const clampLeft = (l: number) => Math.max(MARGIN, Math.min(vw - w - MARGIN, l));
    if (rect.left - GAP - w >= MARGIN)
      return {
        left: rect.left - GAP - w,
        top: clampTop(rect.top + rect.height / 2 - cardH / 2),
        w,
      };
    if (rect.right + GAP + w <= vw - MARGIN)
      return { left: rect.right + GAP, top: clampTop(rect.top + rect.height / 2 - cardH / 2), w };
    if (rect.bottom + GAP + cardH <= vh - MARGIN)
      return { left: clampLeft(rect.left), top: rect.bottom + GAP, w };
    return { left: clampLeft(rect.left), top: clampTop(rect.top - GAP - cardH), w };
  });

  function onkey(e: KeyboardEvent) {
    if (e.key === 'Escape') ctl.endTour();
    else if (e.key === 'ArrowRight' || e.key === 'Enter') next();
    else if (e.key === 'ArrowLeft') go(index - 1);
    else return;
    e.preventDefault();
    e.stopPropagation();
  }
</script>

<svelte:window onkeydown={onkey} onresize={measure} />

<!-- Blocks clicks on the page while the tour is showing. -->
<div class="shade" class:dim={!rect} aria-hidden="true"></div>
{#if rect}
  <div
    class="spot"
    aria-hidden="true"
    style:left={`${rect.left - 6}px`}
    style:top={`${rect.top - 6}px`}
    style:width={`${rect.width + 12}px`}
    style:height={`${rect.height + 12}px`}
  ></div>
{/if}
<div
  class="card"
  bind:this={card}
  role="dialog"
  aria-modal="true"
  aria-labelledby="tour-title"
  aria-describedby="tour-body"
  data-testid="tour"
  data-step={step.id}
  style:left={`${pos.left}px`}
  style:top={`${pos.top}px`}
  style:width={`${pos.w}px`}
>
  {#if step.image}
    <img
      class="shot"
      src={`${import.meta.env.BASE_URL}${step.image.src}`}
      alt={step.image.alt}
      width="640"
      height="400"
      data-testid="tour-image"
      onload={measure}
    />
  {/if}
  <div class="count" data-testid="tour-count">{index + 1} / {TOUR_STEPS.length}</div>
  <h2 id="tour-title">{step.title}</h2>
  <p id="tour-body">
    {#each tourBodyParts(step.body) as part, i (i)}{#if part.icon}<Icon
          name={part.icon}
        />{:else if part.key}<kbd>{part.text}</kbd>{:else}{part.text}{/if}{/each}
  </p>
  <div class="dots" aria-hidden="true">
    {#each TOUR_STEPS as s, i (s.id)}<i class:on={i === index}></i>{/each}
  </div>
  <div class="actions">
    <button class="link" data-testid="tour-skip" onclick={() => ctl.endTour()}
      >{index === 0 ? 'Skip tour' : 'End tour'}</button
    >
    <span class="grow"></span>
    {#if index > 0}<button data-testid="tour-back" onclick={() => go(index - 1)}>Back</button>{/if}
    <button class="primary" bind:this={nextButton} data-testid="tour-next" onclick={next}
      >{last ? 'Finish' : 'Next'}</button
    >
  </div>
</div>

<style>
  .shade {
    position: fixed;
    inset: 0;
    z-index: 60;
  }
  .shade.dim {
    background: rgba(5, 5, 8, 0.62);
  }
  .spot {
    position: fixed;
    z-index: 61;
    border-radius: 10px;
    border: 2px solid var(--accent);
    box-shadow: 0 0 0 200vmax rgba(5, 5, 8, 0.62);
    pointer-events: none;
    transition:
      left 0.25s,
      top 0.25s,
      width 0.25s,
      height 0.25s;
  }
  .card {
    position: fixed;
    z-index: 62;
    padding: 16px 18px 14px;
    border-radius: var(--radius);
    border: 1px solid var(--border);
    background: var(--panel-solid);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    transition:
      left 0.25s,
      top 0.25s;
  }
  .shot {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 8 / 5;
    object-fit: cover;
    margin-bottom: 12px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    background: #000;
  }
  /* Short windows: the words matter more than the picture. */
  @media (max-height: 560px) {
    .shot {
      display: none;
    }
  }
  .count {
    font-size: 11px;
    color: var(--muted);
    letter-spacing: 0.06em;
  }
  h2 {
    margin: 4px 0 8px;
    font-size: 16px;
  }
  p {
    margin: 0 0 12px;
    font-size: 13.5px;
    line-height: 1.5;
    color: var(--text);
  }
  kbd {
    font-family: var(--mono);
    font-size: 11.5px;
    padding: 1px 5px;
    border-radius: 5px;
    border: 1px solid var(--border);
    background: rgba(255, 255, 255, 0.06);
    white-space: nowrap;
  }
  .dots {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }
  .dots i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--border);
  }
  .dots i.on {
    background: var(--accent);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .grow {
    flex: 1;
  }
</style>
